/**
 * Notification Preferences Component
 * Settings UI for notification preferences
 */

import React, { useState, useEffect } from "react";
import { useNotifications } from "@/contexts/NotificationContext";
import { Bell, Clock, Mail, Smartphone, Save } from "lucide-react";
import { NotificationCategory } from "@/types/notification";
import "./NotificationPreferences.css";

const NotificationPreferences: React.FC = () => {
    const { preferences, updatePreferences } = useNotifications();
    const [localPreferences, setLocalPreferences] = useState(preferences);
    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState("");

    useEffect(() => {
        setLocalPreferences(preferences);
    }, [preferences]);

    const categories: NotificationCategory[] = [
        NotificationCategory.SYSTEM,
        NotificationCategory.ACHIEVEMENT,
        NotificationCategory.QUIZ,
        NotificationCategory.PRACTICE,
        NotificationCategory.COURSE,
        NotificationCategory.SOCIAL,
        NotificationCategory.PAYMENT,
        NotificationCategory.REMINDER,
    ];

    const channels = ["in_app", "email", "push"];

    const handleToggleEnabled = () => {
        setLocalPreferences((prev) => prev ? { ...prev, enabled: !prev.enabled } : prev);
    };

    const handleChannelToggle = (category: NotificationCategory, channel: string) => {
        setLocalPreferences((prev) => {
            if (!prev) return prev;
            const categoryPrefs = prev.category_preferences[category] || {};
            return {
                ...prev,
                category_preferences: {
                    ...prev.category_preferences,
                    [category]: {
                        ...categoryPrefs,
                        [channel]: !categoryPrefs[channel],
                    },
                },
            };
        });
    };

    const handleQuietHoursChange = (field: "start" | "end", value: string) => {
        setLocalPreferences((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                [`quiet_hours_${field}`]: value || undefined,
            };
        });
    };

    const handleRateLimitChange = (field: "max_emails_per_day" | "max_push_per_hour", value: number) => {
        setLocalPreferences((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                [field]: value,
            };
        });
    };

    const handleSave = async () => {
        if (!localPreferences) return;

        setIsSaving(true);
        setSaveMessage("");

        try {
            await updatePreferences(localPreferences);
            setSaveMessage("Preferences saved successfully!");
            setTimeout(() => setSaveMessage(""), 3000);
        } catch (error) {
            setSaveMessage("Failed to save preferences. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    if (!localPreferences) {
        return <div className="preferences-loading">Loading preferences...</div>;
    }

    return (
        <div className="notification-preferences">
            <div className="preferences-header">
                <h2>Notification Preferences</h2>
                <p>Customize how and when you receive notifications</p>
            </div>

            {/* Master Toggle */}
            <div className="preference-section">
                <div className="section-header">
                    <Bell size={20} />
                    <h3>Notifications</h3>
                </div>
                <div className="preference-item">
                    <div className="item-info">
                        <label>Enable notifications</label>
                        <p>Receive notifications across all channels</p>
                    </div>
                    <label className="toggle-switch">
                        <input
                            type="checkbox"
                            checked={localPreferences.enabled}
                            onChange={handleToggleEnabled}
                        />
                        <span className="toggle-slider" />
                    </label>
                </div>
            </div>

            {/* Quiet Hours */}
            <div className="preference-section">
                <div className="section-header">
                    <Clock size={20} />
                    <h3>Quiet Hours</h3>
                </div>
                <div className="preference-item">
                    <div className="item-info">
                        <label>Do not disturb period</label>
                        <p>Notifications will be delayed during these hours</p>
                    </div>
                    <div className="time-inputs">
                        <input
                            type="time"
                            value={localPreferences.quiet_hours_start || ""}
                            onChange={(e) => handleQuietHoursChange("start", e.target.value)}
                            className="time-input"
                        />
                        <span>to</span>
                        <input
                            type="time"
                            value={localPreferences.quiet_hours_end || ""}
                            onChange={(e) => handleQuietHoursChange("end", e.target.value)}
                            className="time-input"
                        />
                    </div>
                </div>
            </div>

            {/* Rate Limits */}
            <div className="preference-section">
                <div className="section-header">
                    <Mail size={20} />
                    <h3>Rate Limits</h3>
                </div>
                <div className="preference-item">
                    <div className="item-info">
                        <label>Max emails per day</label>
                        <p>Limit the number of email notifications</p>
                    </div>
                    <input
                        type="number"
                        min="1"
                        max="100"
                        value={localPreferences.max_emails_per_day}
                        onChange={(e) => handleRateLimitChange("max_emails_per_day", parseInt(e.target.value))}
                        className="number-input"
                    />
                </div>
                <div className="preference-item">
                    <div className="item-info">
                        <label>Max push notifications per hour</label>
                        <p>Limit the number of push notifications</p>
                    </div>
                    <input
                        type="number"
                        min="1"
                        max="50"
                        value={localPreferences.max_push_per_hour}
                        onChange={(e) => handleRateLimitChange("max_push_per_hour", parseInt(e.target.value))}
                        className="number-input"
                    />
                </div>
            </div>

            {/* Category Preferences */}
            <div className="preference-section">
                <div className="section-header">
                    <Smartphone size={20} />
                    <h3>Notification Channels by Category</h3>
                </div>
                <div className="category-table">
                    <div className="table-header">
                        <div className="category-col">Category</div>
                        <div className="channel-col">In-App</div>
                        <div className="channel-col">Email</div>
                        <div className="channel-col">Push</div>
                    </div>
                    {categories.map((category) => {
                        const categoryPrefs = localPreferences.category_preferences[category] || {};
                        return (
                            <div key={category} className="table-row">
                                <div className="category-col">{category}</div>
                                {channels.map((channel) => (
                                    <div key={channel} className="channel-col">
                                        <label className="checkbox-wrapper">
                                            <input
                                                type="checkbox"
                                                checked={categoryPrefs[channel] !== false}
                                                onChange={() => handleChannelToggle(category, channel)}
                                            />
                                            <span className="checkmark" />
                                        </label>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Save Button */}
            <div className="preferences-footer">
                {saveMessage && (
                    <div className={`save-message ${saveMessage.includes("Failed") ? "error" : "success"}`}>
                        {saveMessage}
                    </div>
                )}
                <button
                    className="save-button"
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    <Save size={18} />
                    {isSaving ? "Saving..." : "Save Preferences"}
                </button>
            </div>
        </div>
    );
};

export default NotificationPreferences;
